import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import { useMemo } from "react";
import { usePracticeTests } from "./usePracticeTests";

/**
 * The starter tests to offer for a skill, and how to start the chosen one.
 *
 * Practice tests must go through `handlePracticeTestSession` — it is the only
 * path that creates a SUMMATIVE session and applies the paywall.
 */
export const useRecommendedTestsForSkill = () => {
	const practiceTestEnabled = useAppStore(
		(state) => state.exam.practiceTest?.enabled,
	);
	const {
		handlePracticeTestSession,
		practiceTestDistribution,
		creatingSession,
		loading,
	} = usePracticeTests();

	const exams = useMemo(() => {
		const enabled = isWeb
			? Boolean(practiceTestEnabled?.web)
			: Boolean(practiceTestEnabled?.mobile);
		return enabled ? practiceTestDistribution?.exams : undefined;
	}, [practiceTestDistribution?.exams, practiceTestEnabled]);

	const startTest = (examId: string) => {
		const exam = (exams ?? []).find(({ id }) => id === examId);
		if (!exam) {
			return;
		}
		return handlePracticeTestSession(
			exam.id,
			exam.status ?? "NOT_STARTED",
			exam.numericId,
			exam.subType === "DIAGNOSTIC",
			exam.hasAccess,
			false,
			exam.collectionId,
			// Never bypass the paywall — a locked test should still upsell.
			false,
			// Resume an in-progress test directly: this flow renders no resume dialog.
			true,
		);
	};

	return {
		startTest,
		starting: creatingSession,
		exams,
		// Initial load only: `loading` includes isRefetching, and window-focus
		// refetches must not re-skeleton the tiles.
		examsLoading: loading && practiceTestDistribution === undefined,
	};
};
